import type { User } from '@supabase/supabase-js';
import { getSupabaseClient } from '@/lib/supabase/loader';

/**
 * Fetch roles and account_type from the profiles table and merge them
 * into the User object so every redirect / role-check sees the DB truth.
 *
 * This is the **single source of truth** for role enrichment.
 * Call this before any redirect-decision or role-gate that relies on user metadata.
 * 
 * The function handles multiple fallback strategies:
 * 1. Try get_user_roles RPC (security definer, bypasses RLS)
 * 2. Fall back to direct table query
 * 3. Return user with existing metadata if all queries fail
 */
export async function enrichUserWithProfileRoles(user: User | null): Promise<User | null> {
  if (!user?.id) return user;

  try {
    const supabase = await getSupabaseClient();
    
    // Strategy 1: Use the get_user_roles security-definer function
    // This bypasses RLS and avoids permission issues during login
    try {
      const { data: result, error } = await supabase.rpc('get_user_roles', {
        user_id: user.id,
      });

      if (!error && result) {
        // get_user_roles returns TABLE which Supabase serialises as an array of rows.
        // Handle both array-of-rows and single-object shapes for robustness.
        const row = Array.isArray(result) ? result[0] : result;
        const dbRoles = Array.isArray(row?.roles) ? row.roles : [];
        const dbAccountType = row?.account_type ?? user.user_metadata?.account_type ?? user.app_metadata?.account_type;

        // Successfully enriched with DB data
        if (dbRoles.length > 0 || dbAccountType) {
          const enrichedUser = {
            ...user,
            app_metadata: {
              ...user.app_metadata,
              roles: dbRoles,
              account_type: dbAccountType,
            },
            user_metadata: {
              ...user.user_metadata,
              roles: dbRoles,
              account_type: dbAccountType,
            },
          };
          
          console.log('[enrichUserWithProfileRoles] Successfully enriched via get_user_roles RPC:', {
            userId: user.id,
            email: user.email,
            roles: dbRoles,
            account_type: dbAccountType,
          });
          
          return enrichedUser;
        }
      } else if (error) {
        // Log but continue to fallback strategy
        console.warn('[enrichUserWithProfileRoles] RPC error (will try fallback):', {
          code: error.code,
          message: error.message,
        });
      }
    } catch (rpcErr) {
      console.warn('[enrichUserWithProfileRoles] RPC call failed:', rpcErr instanceof Error ? rpcErr.message : String(rpcErr));
    }

    // Strategy 2: Fall back to direct table query
    const { data: profile, error: tableError } = await supabase
      .from('profiles')
      .select('roles, account_type')
      .eq('id', user.id)
      .single();

    if (tableError) {
      console.warn('[enrichUserWithProfileRoles] Table query failed:', {
        code: tableError.code,
        message: tableError.message,
      });
      // Return user with existing metadata
      return user;
    }

    if (!profile) {
      console.warn('[enrichUserWithProfileRoles] No profile record found:', user.id);
      return user;
    }

    const dbRoles = Array.isArray(profile.roles) ? profile.roles : [];
    const dbAccountType = profile.account_type ?? user.user_metadata?.account_type ?? user.app_metadata?.account_type;

    // Only enrich if we have something new
    if (dbRoles.length === 0 && !dbAccountType) {
      console.log('[enrichUserWithProfileRoles] Profile has no roles or account_type:', user.id);
      return user;
    }

    // Merge DB roles into both metadata bags so every consumer sees them
    const enrichedUser = {
      ...user,
      app_metadata: {
        ...user.app_metadata,
        roles: dbRoles,
        account_type: dbAccountType,
      },
      user_metadata: {
        ...user.user_metadata,
        roles: dbRoles,
        account_type: dbAccountType,
      },
    };

    console.log('[enrichUserWithProfileRoles] Successfully enriched via table query:', {
      userId: user.id,
      email: user.email,
      roles: dbRoles,
      account_type: dbAccountType,
    });

    return enrichedUser;
  } catch (err) {
    console.error('[enrichUserWithProfileRoles] Unexpected error:', err);
    // Return user with whatever metadata they already have
    return user;
  }
}

/**
 * Compute where to send a user after sign-in.
 * 
 * Routing hierarchy:
 * 1. Admins → /admin (always, unless they explicitly navigate elsewhere)
 * 2. Partners → /partner
 * 3. Enterprise → /enterprise  
 * 4. Individuals/Institutions → /app
 * 
 * - Protects against open-redirects by ignoring absolute URLs
 * - Logs detailed routing decisions for debugging
 * - Respects users explicitly choosing to visit different zones
 */
export function computePostLoginRedirect(from: string | undefined, user: User | null): string {
  const defaultDest = '/app';
  
  // Validate from parameter - only allow internal paths
  const safeFrom = (typeof from === 'string' && from.startsWith('/') && !from.includes('://')) ? from : defaultDest;

  // Extract user account metadata
  const userMetadata = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const appMetadata = (user?.app_metadata ?? {}) as Record<string, unknown>;

  // Get account type from either metadata source
  const accountType = (userMetadata.account_type as string | null) ?? (appMetadata.account_type as string | null) ?? null;
  
  // Extract roles - check multiple possible locations for compatibility
  const rolesSet = new Set<string>();

  // Check for roles array in both metadata sources
  if (Array.isArray(appMetadata.roles)) {
    for (const role of appMetadata.roles as string[]) {
      rolesSet.add(role);
    }
  }
  if (Array.isArray(userMetadata.roles)) {
    for (const role of userMetadata.roles as string[]) {
      rolesSet.add(role);
    }
  }

  // Check for single role in either metadata source (for backward compatibility)
  const singleRole = (appMetadata.role as string | null) ?? (userMetadata.role as string | null) ?? null;
  if (singleRole) {
    rolesSet.add(singleRole);
  }

  const roles = Array.from(rolesSet);

  // Identify user type based on account_type and roles
  // Use BOTH account_type AND roles so that users with only account_type='partner'
  // (e.g. testpartner@bank.local) are still correctly classified.
  const isAdmin = roles.includes('platform_admin');
  const isPartner = accountType === 'partner' || 
                    roles.some(r => r.startsWith('partner_'));
  const isEnterprise = accountType === 'institution' || 
                       roles.some(r => r.startsWith('org_'));
  const isConsumer = accountType === 'individual' || 
                     roles.some(r => r.startsWith('consumer') || r === 'executor' || r === 'emergency_contact') ||
                     (!isAdmin && !isPartner && !isEnterprise && roles.length === 0);

  console.log('[computePostLoginRedirect] User classification:', {
    email: user?.email,
    accountType,
    roles,
    isAdmin,
    isPartner,
    isEnterprise,
    isConsumer,
    requestedPath: from || '(default)',
    safePath: safeFrom,
  });

  // PRIORITY 1: Platform admins go to /admin
  // Allow admins to explicitly navigate to other zones if they start with /app, /, or /partner
  if (isAdmin) {
    if (safeFrom.startsWith('/admin')) {
      console.log('[computePostLoginRedirect] Admin routing to explicit admin path:', safeFrom);
      return safeFrom;
    }
    if (safeFrom === defaultDest || safeFrom === '/' || safeFrom === '/app' || safeFrom === '/partner') {
      console.log('[computePostLoginRedirect] Admin routing to /admin (from default path)');
      return '/admin';
    }
    console.log('[computePostLoginRedirect] Admin respecting explicit path:', safeFrom);
    return safeFrom;
  }

  // PRIORITY 2: Partners go to /partner
  // Partners default to /partner, but we respect explicit safe deep-links.
  if (isPartner) {
    // If they're explicitly going to partner, honor it
    if (safeFrom.startsWith('/partner')) {
      console.log('[computePostLoginRedirect] Partner routing to explicit partner path:', safeFrom);
      return safeFrom;
    }

    // Avoid routing non-admin partners into restricted zones.
    if (safeFrom.startsWith('/admin') || safeFrom.startsWith('/enterprise')) {
      console.log('[computePostLoginRedirect] Partner requested restricted zone, routing to /partner:', safeFrom);
      return '/partner';
    }

    // If the request is from the default/root/app shell, force /partner
    if (safeFrom === defaultDest || safeFrom === '/' || safeFrom === '/app') {
      console.log('[computePostLoginRedirect] Partner routing to /partner (from default path)');
      return '/partner';
    }

    // Otherwise, respect the explicit safe deep-link (e.g. /app/plan-demo)
    console.log('[computePostLoginRedirect] Partner respecting explicit path:', safeFrom);
    return safeFrom;
  }

  // PRIORITY 3: Enterprise users route to /enterprise
  if (isEnterprise) {
    if (safeFrom.startsWith('/enterprise')) {
      console.log('[computePostLoginRedirect] Enterprise routing to explicit enterprise path:', safeFrom);
      return safeFrom;
    }
    if (safeFrom === defaultDest || safeFrom === '/' || safeFrom === '/app') {
      console.log('[computePostLoginRedirect] Enterprise routing to /enterprise (from default)');
      return '/enterprise';
    }
    console.log('[computePostLoginRedirect] Enterprise respecting explicit path:', safeFrom);
    return safeFrom;
  }

  // PRIORITY 4: Default behavior for consumers
  if (isConsumer) {
    console.log('[computePostLoginRedirect] Consumer routing to:', safeFrom || defaultDest);
    return safeFrom || defaultDest;
  }

  // FALLBACK: If no specific type matched, use the requested path or default
  console.log('[computePostLoginRedirect] No specific type matched, using:', safeFrom || defaultDest);
  return safeFrom || defaultDest;
}
